import {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useReducer,
  useState,
} from 'react';
import {
  GF_CheckboxInput,
  GF_EmailInput,
  GF_FormSubmitResult,
} from '../model/gforms/gform';
import { useLogger } from '../services/LogService';
import { ModelsService } from '../services/ModelsService';
import { AppContext } from '../state/AppContextProvider';
import { useWP } from './WPHooks';

export interface FieldValue {
  id: number;
}

export interface CheckboxFieldValue extends FieldValue {
  checkboxValues: GF_CheckboxInput[];
}

export interface EmailFieldValue extends FieldValue {
  emailValues: GF_EmailInput;
}

export interface StringFieldValue extends FieldValue {
  value: string;
}

export interface StringFieldValues extends FieldValue {
  values: string[];
}

export type FieldValueUnion =
  | CheckboxFieldValue
  | EmailFieldValue
  | StringFieldValue
  | StringFieldValues;

interface Action {
  type: ACTION_TYPES;
  fieldValue: FieldValueUnion;
}

export enum ACTION_TYPES {
  updateCheckboxFieldValue = 'updateCheckboxFieldValue',
  updateEmailFieldValue = 'updateEmailFieldValue',
  updateMultiSelectFieldValue = 'updateMultiSelectFieldValue',
  updatePhoneFieldValue = 'updatePhoneFieldValue',
  updateRadioFieldValue = 'updateRadioFieldValue',
  updateSelectFieldValue = 'updateSelectFieldValue',
  updateTextAreaFieldValue = 'updateTextAreaFieldValue',
  updateTextFieldValue = 'updateTextFieldValue',
}

function reducer(state: FieldValueUnion[], action: Action) {
  const getOtherFieldValues = (id: number) =>
    state.filter((fieldValue) => fieldValue.id !== id);

  switch (action.type) {
    case ACTION_TYPES.updateCheckboxFieldValue: {
      const { id, checkboxValues } = action.fieldValue as CheckboxFieldValue;
      return [...getOtherFieldValues(id), { id, checkboxValues }];
    }
    case ACTION_TYPES.updateEmailFieldValue: {
      const { id, emailValues } = action.fieldValue as EmailFieldValue;
      return [...getOtherFieldValues(id), { id, emailValues }];
    }
    case ACTION_TYPES.updateMultiSelectFieldValue: {
      const { id, values } = action.fieldValue as StringFieldValues;
      return [...getOtherFieldValues(id), { id, values }];
    }
    case ACTION_TYPES.updatePhoneFieldValue:
    case ACTION_TYPES.updateRadioFieldValue:
    case ACTION_TYPES.updateSelectFieldValue:
    case ACTION_TYPES.updateTextAreaFieldValue:
    case ACTION_TYPES.updateTextFieldValue: {
      const { id, value } = action.fieldValue as StringFieldValue;
      return [...getOtherFieldValues(id), { id, value }];
    }
    default:
      throw new Error(
        `Field value update operation not supported: ${action.type}.`
      );
  }
}

const DEFAULT_STATE: FieldValueUnion[] = [];

const GFormsContext = createContext<{
  state: FieldValueUnion[];
  dispatch: Dispatch<Action>;
}>({
  state: DEFAULT_STATE,
  dispatch: () => null,
});

export function GravityFormProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE);

  return (
    <GFormsContext.Provider value={{ state, dispatch }}>
      {children}
    </GFormsContext.Provider>
  );
}

const useGravityForm = () => useContext(GFormsContext);

export default useGravityForm;

export const useGFormsSubmit = () => {
  const { wp } = useWP();

  const { state } = useContext(AppContext);

  const logger = useLogger();

  const [result, setResult] = useState<GF_FormSubmitResult>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<{ message: string } | undefined>(
    undefined
  );

  const submitForm = async (formData: {
    formId: number;
    fieldValues: FieldValueUnion[];
  }) => {
    const { formId, fieldValues } = formData;

    logger.info(`submitForm() with id = ${formId}`);

    setLoading(true);
    setError(undefined);

    const uuid_hashed = await ModelsService.hash(
      state.user?.username || '',
      'SHA-256'
    );

    let isAnonymousForm = false;

    // build data from fieldValues
    const data = {};
    fieldValues.map((fieldValue) => {
      const key = `input_${fieldValue.id}`;
      // text, textarea, phone, select
      if (fieldValue['value'] !== undefined) {
        if (fieldValue['value'].includes('{hash(user:user_login)}')) {
          isAnonymousForm = true;
          data[key] = fieldValue['value'].replace(
            '{hash(user:user_login)}',
            uuid_hashed
          );
        } else {
          data[key] = fieldValue['value'];
        }
      }
      // checkbox
      if (fieldValue['checkboxValues'] !== undefined) {
        fieldValue['checkboxValues'].map((v: GF_CheckboxInput) => {
          const k = `${v.inputId}`.replace('.', '_');
          data[`input_${k}`] = v.value;
          return true;
        });
      }
      // email
      if (fieldValue['emailValues'] !== undefined) {
        data[key] = fieldValue['emailValues']['value'];
      }
      return true;
    });
    logger.debug('submitForm() fieldValues =', fieldValues);

    const promise: Promise<GF_FormSubmitResult> = wp
      .submitGForm(state.locale, formId, data, isAnonymousForm)
      .then((formResult) => {
        setResult(formResult);
        return formResult;
      })
      .catch((e) => {
        setError(e);
        throw e;
      })
      .finally(() => setLoading(false));

    logger.info(`submitForm() DONE with id = ${formId}`);

    return promise;
  };

  return {
    submitForm: submitForm,
    result: result,
    loading: loading,
    error: error,
  };
};
